import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle, PrinterIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useQuotationInvoiceDetailQuery } from "../api/queries";
import {
  DocumentPdfPreviewTour,
  useDocumentPdfPreviewTour,
} from "../tour/DocumentPdfPreviewTour";
import { DocumentPdfPreviewContent } from "../ui/DocumentPdfPreviewContent";

const documentTypeLabel: Record<string, string> = {
  quotation: "Quotation",
  invoice: "Invoice",
  receipt: "Receipt",
};

export function QuotationInvoiceDocumentPdfPreviewPage() {
  const { id } = useParams({ from: "/app/documents/$id/pdf-preview" });
  const nav = useNavigate({ from: "/app/documents/$id/pdf-preview" });
  const search = useSearch({ from: "/app/documents/$id/pdf-preview" });

  const { data, isLoading, error } = useQuotationInvoiceDetailQuery(id ?? "");
  const hasTriggeredPrint = useRef(false);

  // Set document title for print/save-as-PDF filename
  useEffect(() => {
    if (data?.document && id) {
      const docNumber =
        data.document.documentNumber ?? data.document.id.slice(0, 8);
      const typeLabel =
        documentTypeLabel[data.document.documentType] ?? "Document";
      document.title = `${typeLabel}-${docNumber}`;
    }
    return () => {
      document.title = "freedom® Admin";
    };
  }, [data?.document, id]);

  // Auto-print when opened with ?print=1 (e.g. from detail page Print button)
  useEffect(() => {
    if (!data || !search.print || hasTriggeredPrint.current) return;
    hasTriggeredPrint.current = true;
    window.print();
    nav({
      to: "/app/documents/$id/pdf-preview",
      params: { id: id ?? "" },
      search: { print: undefined },
    });
  }, [data, search.print, id, nav]);

  const handleBack = () => {
    nav({ to: "/app/documents/$id", params: { id: id ?? "" } });
  };

  const handlePrint = () => {
    window.print();
  };

  const tour = useDocumentPdfPreviewTour();

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບເອກະສານ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ເອກະສານທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button
                onClick={() =>
                  nav({
                    to: "/app/documents",
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined,
                      filters: undefined,
                    },
                  })
                }
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປລາຍການເອກະສານ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <DocumentPdfPreviewTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {/* Header Actions - Hidden in print */}
        <div
          className="mb-6 flex items-center justify-between print:hidden"
          data-tourid="document-pdf-actions"
        >
          <div className="flex items-center space-x-2">
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປທີ່ເອກະສານ
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <PrinterIcon className="h-4 w-4" />
              ພິມ
            </Button>
          </div>
        </div>

        {/* PDF Preview Content */}
        <div data-tourid="document-pdf-content">
          <DocumentPdfPreviewContent
            document={data.document}
            items={data.items}
            payments={data.payments}
          />
        </div>
      </Main>
    </>
  );
}
